import { Card, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { FileClock, Package, PlusCircle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setSsSkuSelectionModalTestId } from "./ss-sku-selection-modal.utils";

interface SsSkuSelectionModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSelectNewSku: () => void;
  readonly onSelectNewBagSize: () => void;
  readonly onSelectRejectedProducts: () => void;
  readonly onSelectDrafts: () => void;
  readonly testId?: string;
}

export function SsSkuSelectionModal({
  open,
  onOpenChange,
  onSelectNewSku,
  onSelectNewBagSize,
  onSelectRejectedProducts,
  onSelectDrafts,
  testId,
}: SsSkuSelectionModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent testId={testId ?? setSsSkuSelectionModalTestId("content")} className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle data-testid={setSsSkuSelectionModalTestId("title")}>{t("ssRoaster.skuSelection.title")}</DialogTitle>
          <DialogDescription>{t("ssRoaster.skuSelection.description")}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
          <Card
            className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
            onClick={onSelectNewSku}
            data-testid={setSsSkuSelectionModalTestId("new-sku-card")}
          >
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-primary/10 p-3">
                  <PlusCircle className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">{t("ssRoaster.skuSelection.newSku")}</CardTitle>
                  <CardDescription className="mt-2">{t("ssRoaster.skuSelection.newSkuDescription")}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card
            className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
            onClick={onSelectNewBagSize}
            data-testid={setSsSkuSelectionModalTestId("new-bag-size-card")}
          >
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-blue-500/10 p-3">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">{t("ssRoaster.skuSelection.newBagSize")}</CardTitle>
                  <CardDescription className="mt-2">{t("ssRoaster.skuSelection.newBagSizeDescription")}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card
            className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
            onClick={onSelectRejectedProducts}
            data-testid={setSsSkuSelectionModalTestId("rejected-products-card")}
          >
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-red-500/10 p-3">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">{t("ssRoaster.skuSelection.rejectedProducts")}</CardTitle>
                  <CardDescription className="mt-2">{t("ssRoaster.skuSelection.rejectedProductsDescription")}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card
            className="cursor-pointer transition-all hover:border-primary hover:shadow-md"
            onClick={onSelectDrafts}
            data-testid={setSsSkuSelectionModalTestId("drafts-card")}
          >
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-amber-500/10 p-3">
                  <FileClock className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">{t("ssRoaster.skuSelection.viewDrafts")}</CardTitle>
                  <CardDescription className="mt-2">{t("ssRoaster.skuSelection.viewDraftsDescription")}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
